import React, { useState, useEffect, useCallback, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";

import get from "lodash/get";
import { useFormik } from "formik";

import {
  createInvoiceAction,
  invoiceGetAllActionById,
} from "actions/invoiceActions";
import { addInvoiceForm, createFieldExtraProps } from "./addInvoiceUtils";
import FormInputField from "components/TextFields/FormInputField";
import AppRoleContext from "context/AppRoleContext";
import {
  isAssignee,
  isProjectManager,
  isSupplierAdmin,
} from "core/utils/roleUtils";
import { getProjectData } from "selectors/projectSelector";
import { fetchHomeownersAction } from "actions/propertyActions";
import { getHomeOwners } from "selectors/propertySelectors";
import NewInvoiceItem from "./NewInvoiceItem";
import PreviewInvoice from "./PreviewInvoice";
import "./invoice.scss";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  FormHelperText,
  Typography,
} from "@mui/material";
import { Close as CloseIcon, Print } from "@mui/icons-material";
import { startCase } from "lodash";
import { showAppApiAlert } from "actions/appActions";
import { jobGetAll } from "actions/jobActions";
import { getJobsList } from "selectors/jobSelectors";
import { getUserProfileDetails } from "selectors/appSelector";
import { getInvoiceData } from "selectors/invoiceSelector";
import DownloadPdf from "components/Downloads/CreatePDF";

const AddNewInvoice = ({
  invoiceId,
  selectedInvoice = {},
  onClose,
  onSave,
  params = {},
}) => {
  const dispatch = useDispatch();

  const appRoleDetails = useContext(AppRoleContext);
  const projectData = useSelector(getProjectData);
  const userProfileData = useSelector(getUserProfileDetails);
  const invoiceData = useSelector(getInvoiceData);

  const { homeOwnersList } = useSelector(getHomeOwners);
  const { jobsList } = useSelector(getJobsList);

  const dynamicOptions = {
    billTo: homeOwnersList.map((homeOwner) => ({
      key: homeOwner.userId,
      value: homeOwner.userName,
      text: homeOwner.userName,
      data: homeOwner,
    })),
    jobId: jobsList.map((job) => ({
      key: job.jobId,
      value: job.jobName,
      text: job.jobName,
      data: job,
    })),
  };
  // const buildersList = useSelector()

  // Support invoiceId coming either directly as prop or nested in selectedInvoice
  const computedInvoiceId =
    invoiceId || selectedInvoice?.data?.invoiceId || selectedInvoice?.invoiceId;

  useEffect(() => {
    if (computedInvoiceId) {
      dispatch(invoiceGetAllActionById(computedInvoiceId));
    }
  }, [dispatch, computedInvoiceId]);

  const isSupplier =
    isSupplierAdmin(appRoleDetails) || isAssignee(projectData.accessType);

  const isBuilder =
    isProjectManager(appRoleDetails, projectData?.projUserRole) &&
    !isAssignee(projectData.accessType);

  const [invoiceItems, updateInvoiceItems] = useState([
    {
      id: Date.now(),
      itemName: "",
      unit: "",
      quantity: 0,
      price: 0.0,
      discPct: 0,
      taxPct: 0,
      totalAmount: 0.0,
      amount: 0.0,
      taxAmount: 0.0,
    },
  ]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [printInvoice, setPrintInvoice] = useState(false);
  const { formFields, validationSchema } = addInvoiceForm;

  useEffect(() => {
    const itemsList = invoiceData?.invoiceItemsList || [];
    updateInvoiceItems(
      itemsList.map((item) => ({ ...item, id: item.invoiceItemId || item.id }))
    );
  }, [invoiceData]);

  const formik = useFormik({
    initialValues: addInvoiceForm.defaultState(invoiceData),
    enableReinitialize: true, // allow form to populate after async fetch
    validationSchema: validationSchema,
    onSubmit: () => {
      if (
        invoiceItems.length > 0 &&
        invoiceItems[0].itemName &&
        invoiceItems[0].amount > 0
      ) {
        dispatch(
          createInvoiceAction({
            formData: {
              ...formik.values,
              totalAmount: parseFloat(
                invoiceItems.reduce((acc, item) => acc + (item.amount || 0), 0)
              ).toFixed(2),
              invoiceStatusId: 1,
              projectId: projectData.projectId,
              propertyId: projectData.property?.propertyId || null,
              invoiceItemsList: invoiceItems,
              billFrom: userProfileData.userName,
              billFromEmail: userProfileData.email,
            },
            onSuccess: (payload) => {
              // navigate("/jobs");
              onClose(false);
              if (onSave) {
                onSave(payload);
              }
            },
            params,
            // payload: propData,
          })
        );
      } else {
        dispatch(
          showAppApiAlert({
            showAlert: true,
            type: "error",
            message:
              "Please add at least one invoice item with a valid amount and item name.",
          })
        );
      }
    },
  });

  const fetchRecieptentOptions = useCallback(() => {
    const queryData = {
      pageNumber: 1,
      pageSize: 500,
      projectId: projectData.projectId,
    };
    dispatch(jobGetAll(queryData));
    if (isBuilder) {
      dispatch(fetchHomeownersAction({ pageSize: 500, pageNumber: 1 }));
    }
    if (isSupplier) {
      formik.setFieldValue("clientType", "contractor");
      formik.setFieldValue(
        "billTo",
        get(projectData, "company.companyName", "")
      );
      formik.setFieldValue("billToEmail", get(projectData, "createdBy", ""));
    }
    formik.setFieldValue("poNumber", get(projectData, "projectCode", ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isBuilder, isSupplier]);

  useEffect(() => {
    fetchRecieptentOptions();
  }, [fetchRecieptentOptions]);

  const updateDefaultFields = useCallback(() => {
    if (projectData && projectData.projectId) {
      if (selectedInvoice && selectedInvoice.data?.invoiceId) {
        return;
      } else {
        const assignedHomeowner = get(projectData, "property.userId", null);
        const getAssignedHomeownerDetails = homeOwnersList.find(
          (homeOwner) => homeOwner.userId === assignedHomeowner
        );
        if (getAssignedHomeownerDetails) {
          formik.setFieldValue(
            "billTo",
            getAssignedHomeownerDetails.userName || ""
          );
          formik.setFieldValue(
            "billToEmail",
            getAssignedHomeownerDetails?.email || ""
          );
          formik.setFieldValue(
            "billToPhone",
            getAssignedHomeownerDetails?.mobileNumber || ""
          );
          // formik.setFieldTouched("billToEmail", true);
          formik.setFieldValue(
            "userId",
            getAssignedHomeownerDetails?.userId || ""
          );
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeOwnersList, projectData]);

  useEffect(() => {
    updateDefaultFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateDefaultFields]);

  return (
    <>
      <div className="p-2 row create-invoice-container">
        <div className="invoice-form col-12 w-100">
          <div className="stepContent container-fluid row">
            {formFields.map((field, index) => {
              const extraInputProps = get(field, "extraInputProps", {});

              let extraProps =
                typeof extraInputProps === "function"
                  ? extraInputProps(formik)
                  : extraInputProps;

              if (createFieldExtraProps[field.key]) {
                extraProps = {
                  ...extraProps,
                  ...createFieldExtraProps[field.key][
                    isBuilder ? "builder" : "supplier"
                  ](formik),
                };
              }

              return (
                <FormInputField
                  field={{
                    ...field,
                    options: dynamicOptions[field.key] || field.options,
                    required: field.required || extraProps?.required,
                  }}
                  formik={formik}
                  value={formik.values[field.key]}
                  key={field.key}
                  id={field.id}
                  dynamicOptions={dynamicOptions}
                  {...extraProps}
                />
              );
            })}
          </div>
          <div>
            <NewInvoiceItem
              items={invoiceItems}
              setItems={updateInvoiceItems}
            />
          </div>
          <div>
            <Typography variant="body1" className="mt-1 ">
              <sup style={{ color: "red", fontSize: "1em", top: 0 }}>*</sup>
              <strong>Required Fields</strong>
            </Typography>
          </div>
          <div>
            {formik.errors && Object.keys(formik.errors).length > 0 && (
              <div className="error-message">
                <FormHelperText style={{ fontSize: "12px" }} error>
                  Please fill all the required fields{" "}
                  {Object.keys(formik.errors)
                    .map((key, index) => {
                      return startCase(key);
                    })
                    .join(", ")}
                </FormHelperText>
              </div>
            )}
          </div>

          <div className="mt-4 d-flex justify-content-center">
            <button className="secondaryButton" type="button" onClick={onClose}>
              Cancel
            </button>
            <div className="text-center mx-2">
              <button
                className="primaryButton"
                type="button"
                onClick={() => setShowPreviewModal(true)}
              >
                Show Invoice Preview
              </button>
            </div>
            <button
              className="primaryButton"
              style={{ color: "white", backgroundColor: "green" }}
              type="button"
              onClick={formik.handleSubmit}
            >
              {invoiceData ? "Update Invoice" : "Create Invoice"}
            </button>
          </div>
        </div>
      </div>

      {printInvoice && (
        <DownloadPdf
          shouldSavePDF={true}
          onDownloadComplete={() => setPrintInvoice(false)}
          fileName={invoiceData?.invoiceNumber || "Invoice.pdf"}
          contentInlineStyles={{}}
        >
          {/* Pass RenderTemplate and other content to the PDF */}
          <div>
            {/* <Typography className="h4">
              Invoice Receipt - {invoiceData?.invoiceNumber}
            </Typography>
            <Divider className="divider" /> */}
            <PreviewInvoice
              formData={formik.values}
              invoiceItems={invoiceItems}
              isModal={false}
            />
          </div>
        </DownloadPdf>
      )}

      {/* Mobile Preview Modal */}
      <Dialog
        open={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        maxWidth="md"
        fullWidth
        fullScreen
      >
        <DialogTitle>
          Invoice Preview
          {formik.values?.invoiceId && (
            <IconButton
              onClick={() => {
                setPrintInvoice(true);
              }}
            >
              <Print style={{ fontSize: "1.5rem", color: "#345DAE" }} />
            </IconButton>
          )}
          <IconButton
            aria-label="close"
            onClick={() => setShowPreviewModal(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <PreviewInvoice
            formData={formik.values}
            invoiceItems={invoiceItems}
            isModal={true}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddNewInvoice;
